import { useState } from 'react'
import { complianceService, type DataAccessExport } from '../services/compliance'
import {
  ShieldCheck, UserX, Download, AlertCircle, CheckCircle, X,
  ChevronDown, ChevronRight,
} from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { authService } from '../../identity/services/auth'
import { UserRole } from '../../../types'

type Tab = 'consent' | 'erasure' | 'data-access'

// Only IH and DP are data controllers — they can erase data and export records.
// TC and TL can only manage consent (they interact with customers directly).
const CONTROLLER_ROLES: UserRole[] = [UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL]

export default function CompliancePage() {
  const currentRole = authService.getCurrentUser()?.role as UserRole | undefined
  const isController = currentRole ? CONTROLLER_ROLES.includes(currentRole) : false

  const [tab, setTab] = useState<Tab>('consent')

  // ── Consent ────────────────────────────────────────────────────────────────
  const [consentMobile, setConsentMobile] = useState('')
  const [consentMobileError, setConsentMobileError] = useState('')
  const [consentFor, setConsentFor] = useState('policy_processing')
  const [consentResult, setConsentResult] = useState<string | null>(null)
  const [consentError, setConsentError] = useState<string | null>(null)
  const [consentLoading, setConsentLoading] = useState(false)

  const [withdrawMobile, setWithdrawMobile] = useState('')
  const [withdrawMobileError, setWithdrawMobileError] = useState('')
  const [withdrawResult, setWithdrawResult] = useState<string | null>(null)
  const [withdrawError, setWithdrawError] = useState<string | null>(null)
  const [withdrawLoading, setWithdrawLoading] = useState(false)

  // ── Erasure ────────────────────────────────────────────────────────────────
  const [erasureMobile, setErasureMobile] = useState('')
  const [erasureMobileError, setErasureMobileError] = useState('')
  const [erasureConfirm, setErasureConfirm] = useState(false)
  const [erasureResult, setErasureResult] = useState<{ status: string; leads_erased: number } | null>(null)
  const [erasureError, setErasureError] = useState<string | null>(null)
  const [erasureLoading, setErasureLoading] = useState(false)
  const [showErasureModal, setShowErasureModal] = useState(false)

  // ── Data Access ────────────────────────────────────────────────────────────
  const [accessMobile, setAccessMobile] = useState('')
  const [accessMobileError, setAccessMobileError] = useState('')
  const [accessResult, setAccessResult] = useState<DataAccessExport | null>(null)
  const [accessError, setAccessError] = useState<string | null>(null)
  const [accessLoading, setAccessLoading] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleRecordConsent = async () => {
    const digits = consentMobile.replace(/\D/g, '')
    if (digits.length < 10 || digits.length > 15) {
      setConsentMobileError('Enter a valid 10-digit mobile number')
      return
    }
    setConsentLoading(true)
    setConsentResult(null)
    setConsentError(null)
    try {
      const r = await complianceService.recordConsent(consentMobile, consentFor)
      setConsentResult(
        `Consent recorded for ${r.leads_updated} lead(s) at ${new Date(r.consent_given_at).toLocaleString()}.`
      )
      setConsentMobile('')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setConsentError(detail || 'Failed to record consent')
    } finally {
      setConsentLoading(false)
    }
  }

  const handleWithdrawConsent = async () => {
    const digits = withdrawMobile.replace(/\D/g, '')
    if (digits.length < 10 || digits.length > 15) {
      setWithdrawMobileError('Enter a valid 10-digit mobile number')
      return
    }
    setWithdrawLoading(true)
    setWithdrawResult(null)
    setWithdrawError(null)
    try {
      const r = await complianceService.withdrawConsent(withdrawMobile)
      setWithdrawResult(`Consent withdrawn for ${r.leads_updated} lead(s).`)
      setWithdrawMobile('')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setWithdrawError(detail || 'Failed to withdraw consent')
    } finally {
      setWithdrawLoading(false)
    }
  }

  const handleErasure = async () => {
    const digits = erasureMobile.replace(/\D/g, '')
    if (digits.length < 10 || digits.length > 15) {
      setErasureMobileError('Enter a valid 10-digit mobile number')
      return
    }
    if (!erasureConfirm) return
    setErasureLoading(true)
    setErasureResult(null)
    setErasureError(null)
    setShowErasureModal(false)
    try {
      const r = await complianceService.requestErasure(erasureMobile)
      setErasureResult(r)
      setErasureMobile('')
      setErasureConfirm(false)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErasureError(detail || 'Erasure request failed')
    } finally {
      setErasureLoading(false)
    }
  }

  const handleDataAccess = async () => {
    const digits = accessMobile.replace(/\D/g, '')
    if (digits.length < 10 || digits.length > 15) {
      setAccessMobileError('Enter a valid 10-digit mobile number')
      return
    }
    if (!accessMobile) return
    setAccessLoading(true)
    setAccessResult(null)
    setAccessError(null)
    setExpandedSections({})
    try {
      const r = await complianceService.requestDataAccess(accessMobile)
      setAccessResult(r)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setAccessError(detail || 'Data access request failed')
    } finally {
      setAccessLoading(false)
    }
  }

  const toggleSection = (key: string) =>
    setExpandedSections(s => ({ ...s, [key]: !s[key] }))

  const downloadJson = () => {
    if (!accessResult) return
    const blob = new Blob([JSON.stringify(accessResult as unknown, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `data-export-${accessMobile.replace(/\+/g, '')}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            DPDP Compliance
          </h1>
          <p className="page-subtitle">Consent management, data access requests, and right to erasure</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1" style={{ borderBottom: '1px solid var(--border-default)' }}>
        {([
          ['consent',     'Consent Management', ShieldCheck, false],
          ['erasure',     'Data Erasure',        UserX,      true],
          ['data-access', 'Data Access Export',  Download,   true],
        ] as const).filter(([,, , controllerOnly]) => !controllerOnly || isController).map(([key, label, Icon]) => (
          <button
            key={key}
            className="px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors"
            style={{
              borderBottomColor: tab === key ? 'var(--brand-primary)' : 'transparent',
              color: tab === key ? 'var(--brand-primary)' : 'var(--text-tertiary)',
            }}
            onClick={() => setTab(key)}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Consent Management ─────────────────────────────────────────────── */}
      {tab === 'consent' && (
        <div className="space-y-6">
          {/* Record consent */}
          <div className="card">
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Record Consent
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">
                  Mobile Number <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="tel"
                  className="input"
                  value={consentMobile}
                  onChange={e => {
                    const v = e.target.value.replace(/[^+\d]/g, '')
                    const plusCount = (v.match(/\+/g) || []).length
                    setConsentMobile(plusCount > 1 ? v.replace(/\+/g, '') : v)
                    setConsentMobileError('')
                  }}
                  placeholder="+919876543210 or 9876543210"
                />
                {consentMobileError && (
                  <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{consentMobileError}</p>
                )}
                {!consentMobileError && (
                  <p className="form-hint">Enter the customer's mobile number. Consent is applied to all their leads.</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Consent For</label>
                <select
                  className="input"
                  value={consentFor}
                  onChange={e => setConsentFor(e.target.value)}
                >
                  <option value="policy_processing">Policy Processing</option>
                  <option value="marketing">Marketing</option>
                  <option value="data_sharing">Data Sharing</option>
                </select>
              </div>
            </div>
            <button
              className="btn-primary mt-4"
              onClick={handleRecordConsent}
              disabled={consentLoading || !consentMobile}
            >
              {consentLoading ? 'Recording…' : 'Record Consent'}
            </button>
            {consentResult && (
              <div className="alert-success mt-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 shrink-0" />
                {consentResult}
              </div>
            )}
            {consentError && (
              <div className="alert-danger mt-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {consentError}
              </div>
            )}
          </div>

          {/* Withdraw consent */}
          <div className="card">
            <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              Withdraw Consent
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-tertiary)' }}>
              Revokes all active consents for a customer. Processing activities relying on consent must stop.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">
                  Mobile Number <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="tel"
                  className="input"
                  value={withdrawMobile}
                  onChange={e => {
                    const v = e.target.value.replace(/[^+\d]/g, '')
                    const plusCount = (v.match(/\+/g) || []).length
                    setWithdrawMobile(plusCount > 1 ? v.replace(/\+/g, '') : v)
                    setWithdrawMobileError('')
                  }}
                  placeholder="+919876543210 or 9876543210"
                />
                {withdrawMobileError && (
                  <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{withdrawMobileError}</p>
                )}
                {!withdrawMobileError && (
                  <p className="form-hint">Consent will be withdrawn for all leads under this mobile number.</p>
                )}
              </div>
            </div>
            <button
              className="btn-secondary mt-4"
              onClick={handleWithdrawConsent}
              disabled={withdrawLoading || !withdrawMobile}
            >
              {withdrawLoading ? 'Processing…' : 'Withdraw Consent'}
            </button>
            {withdrawResult && (
              <div className="alert-success mt-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 shrink-0" />
                {withdrawResult}
              </div>
            )}
            {withdrawError && (
              <div className="alert-danger mt-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {withdrawError}
              </div>
            )}
          </div>

          {/* Info banner */}
          <div className="alert-info">
            <strong>DPDP Act 2023:</strong> Consent must be free, specific, informed, unconditional, and unambiguous.
            Withdraw requests must be honoured promptly. Maintain a clear audit trail of all consent operations.
          </div>
        </div>
      )}

      {/* ── Data Erasure ───────────────────────────────────────────────────── */}
      {tab === 'erasure' && (
        <div className="space-y-6">
          <div className="alert-danger flex items-start gap-3">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: 'var(--danger-text)' }} />
            <div>
              <p className="font-semibold text-sm" style={{ color: 'var(--danger-text)' }}>
                Irreversible Action
              </p>
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                Erasure permanently anonymises all PII fields (name, email, mobile) for every lead associated with
                the given mobile number. This action cannot be undone.
              </p>
            </div>
          </div>

          <div className="card">
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Submit Erasure Request
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">
                  Mobile Number <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="tel"
                  className="input"
                  value={erasureMobile}
                  onChange={e => {
                    const v = e.target.value.replace(/[^+\d]/g, '')
                    const plusCount = (v.match(/\+/g) || []).length
                    setErasureMobile(plusCount > 1 ? v.replace(/\+/g, '') : v)
                    setErasureMobileError('')
                  }}
                  placeholder="+919876543210 or 9876543210"
                />
                {erasureMobileError && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{erasureMobileError}</p>}
                {!erasureMobileError && <p className="form-hint">Enter the 10-digit mobile number with or without +91 prefix.</p>}
              </div>
            </div>
            <button
              className="btn-danger mt-4"
              onClick={() => setShowErasureModal(true)}
              disabled={erasureLoading || !erasureMobile}
            >
              {erasureLoading ? 'Processing…' : 'Request Erasure'}
            </button>

            {erasureResult && (
              <div className="alert-success mt-4 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 shrink-0" />
                Erasure complete. {erasureResult.leads_erased} record(s) anonymised.
              </div>
            )}
            {erasureError && (
              <div className="alert-danger mt-4 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {erasureError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Data Access Export ─────────────────────────────────────────────── */}
      {tab === 'data-access' && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              Export Customer Data
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-tertiary)' }}>
              Retrieve all data held for a customer identified by mobile number. Use to fulfil data access requests
              under DPDP Section 11.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">
                  Mobile Number <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="tel"
                  className="input"
                  value={accessMobile}
                  onChange={e => {
                    const v = e.target.value.replace(/[^+\d]/g, '')
                    const plusCount = (v.match(/\+/g) || []).length
                    setAccessMobile(plusCount > 1 ? v.replace(/\+/g, '') : v)
                    setAccessMobileError('')
                  }}
                  placeholder="+919876543210 or 9876543210"
                />
                {accessMobileError && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{accessMobileError}</p>}
              </div>
            </div>
            <button
              className="btn-primary mt-4"
              onClick={handleDataAccess}
              disabled={accessLoading || !accessMobile}
            >
              {accessLoading ? 'Fetching…' : 'Fetch Data'}
            </button>
            {accessError && (
              <div className="alert-danger mt-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {accessError}
              </div>
            )}
          </div>

          {accessResult && (
            <div className="card space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Data Export — {accessMobile}
                  </h3>
                  {accessResult.requested_at && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                      Generated {new Date(accessResult.requested_at as string).toLocaleString()}
                    </p>
                  )}
                </div>
                <button className="btn-secondary" onClick={downloadJson}>
                  <Download className="h-3.5 w-3.5" /> Download JSON
                </button>
              </div>

              {(Object.entries(accessResult) as [string, unknown][]).filter(([section]) => section !== 'requested_at').map(([section, value]) => {
                const isExpanded = expandedSections[section] ?? false
                const count = Array.isArray(value) ? value.length : null
                return (
                  <div key={section} style={{ border: '1px solid var(--border-default)', borderRadius: '8px' }}>
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium"
                      style={{ color: 'var(--text-primary)' }}
                      onClick={() => toggleSection(section)}
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                          : <ChevronRight className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                        }
                        <span className="capitalize">{section.replace(/_/g, ' ')}</span>
                        {count !== null && (
                          <span className="badge-neutral">{count} record{count !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </button>
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid var(--border-default)' }}>
                        <pre
                          className="text-xs p-4 overflow-auto max-h-64"
                          style={{
                            background: 'var(--surface-subtle)',
                            color: 'var(--text-secondary)',
                            borderRadius: '0 0 8px 8px',
                          }}
                        >
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Erasure Confirmation Modal ─────────────────────────────────────── */}
      {showErasureModal && (
        <ModalOverlay onClose={() => { setShowErasureModal(false); setErasureConfirm(false) }}>
          <div className="modal-panel" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Confirm Data Erasure</h2>
              <button
                className="btn-icon"
                onClick={() => { setShowErasureModal(false); setErasureConfirm(false) }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="alert-danger">
                <p className="text-sm">
                  You are about to permanently anonymise all personal data for mobile
                  <strong> {erasureMobile}</strong>. This cannot be reversed.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={erasureConfirm}
                  onChange={e => setErasureConfirm(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  I confirm this erasure request is legitimate and I understand it is irreversible.
                </span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  className="btn-secondary"
                  onClick={() => { setShowErasureModal(false); setErasureConfirm(false) }}
                >
                  Cancel
                </button>
                <button
                  className="btn-danger"
                  onClick={handleErasure}
                  disabled={!erasureConfirm}
                >
                  Erase Permanently
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}
